import {
    Phone24Regular,
    CheckmarkCircle24Regular,
    Clock24Regular,
    Receipt24Regular,
    DismissCircle24Regular,
    QuestionCircle24Regular,
    LockClosed24Regular,
    Building24Regular,
    Mail24Regular
} from "@fluentui/react-icons";
import styles from "./SmsTerms.module.css";

const SmsTerms = () => {
    return (
        <main className={styles.pages}>
            <h1>Base Buddy SMS Terms & Consent</h1>
            <p className={styles.subtitle}>Effective Date: May 20, 2026</p>

            <p>
                Base Buddy is a messaging service operated by Strategi Consulting, LLC that provides military service members, their families,
                and base personnel with instant answers to questions about their installation. This page describes how the SMS service works
                and how you can opt in and out.
            </p>

            {/* What You Receive */}
            <section>
                <h2>
                    <Phone24Regular className={styles.sectionIcon} />
                    What You Receive
                </h2>
                <p>When you opt in to Base Buddy SMS, you will receive:</p>
                <ol>
                    <li>
                        <strong>Responses to questions you send us.</strong> Text any base-related question (e.g., "What are the CDC hours at
                        Edwards?"), and Base Buddy will reply with a sourced answer.
                    </li>
                    <li>
                        <strong>Occasional base information updates</strong> (if applicable). This may include time-sensitive notifications
                        such as gate hour changes, PCS season reminders, or major event announcements relevant to your base.
                    </li>
                </ol>
                <p>
                    Base Buddy will never send you promotional or marketing messages. You will only receive direct replies to your inquiries
                    and the occasional service-relevant base information updates described above.
                </p>
            </section>

            {/* How to Opt In */}
            <section>
                <h2>
                    <CheckmarkCircle24Regular className={styles.sectionIcon} />
                    How to Opt In
                </h2>
                <p>You can opt in to Base Buddy SMS in one of the following ways:</p>
                <ol>
                    <li>
                        <strong>Web form.</strong> Visit the Base Buddy SMS signup page, enter your mobile phone number, and
                        check the consent checkbox.
                    </li>
                    <li>
                        <strong>Text keyword.</strong> Text <strong>START</strong> to <strong>(844) 966-2189</strong> to begin receiving messages.
                    </li>
                </ol>
                <p>After opting in, you will receive a confirmation message.</p>
            </section>

            {/* Message Frequency */}
            <section>
                <h2>
                    <Clock24Regular className={styles.sectionIcon} />
                    Message Frequency
                </h2>
                <p>
                    Message frequency varies based on your activity. You will only receive messages in response to your questions, plus any
                    opt-in notifications you've specifically requested. Typical usage is fewer than 20 messages per month.
                </p>
            </section>

            {/* Message & Data Rates */}
            <section>
                <h2>
                    <Receipt24Regular className={styles.sectionIcon} />
                    Message & Data Rates
                </h2>
                <p>
                    Message and data rates may apply based on your mobile carrier plan. Base Buddy does not charge for the messaging service
                    itself.
                </p>
            </section>

            {/* How to Opt Out */}
            <section>
                <h2>
                    <DismissCircle24Regular className={styles.sectionIcon} />
                    How to Opt Out
                </h2>
                <p>You can opt out of Base Buddy SMS at any time:</p>
                <ol>
                    <li>
                        <strong>Text STOP</strong> to our number. You will receive a final confirmation message and will not receive further
                        messages.
                    </li>
                    <li>
                        Alternate opt-out keywords: <strong>END</strong>, <strong>QUIT</strong>, <strong>UNSUBSCRIBE</strong>,{" "}
                        <strong>CANCEL</strong>.
                    </li>
                </ol>
                <p>
                    To rejoin after opting out, text <strong>START</strong> to the same number.
                </p>
            </section>

            {/* Getting Help */}
            <section>
                <h2>
                    <QuestionCircle24Regular className={styles.sectionIcon} />
                    Getting Help
                </h2>
                <ol>
                    <li>
                        <strong>Text HELP</strong> to our number for a response with support information.
                    </li>
                    <li>
                        <strong>Email:</strong> <a href="mailto:hello@basebuddy.ai">hello@basebuddy.ai</a>
                    </li>
                </ol>
            </section>

            {/* Privacy */}
            <section>
                <h2>
                    <LockClosed24Regular className={styles.sectionIcon} />
                    Privacy
                </h2>
                <p>
                    Base Buddy collects your mobile phone number and the content of messages you send us in order to provide the service.
                    <strong> We do not sell, share, or otherwise disclose your mobile number or message content to any third party,
                    affiliate, or marketing partner.</strong> For full details, see our <a href="#/privacy_policy">Privacy Policy</a>.
                </p>
            </section>

            {/* About Base Buddy */}
            <section>
                <h2>
                    <Building24Regular className={styles.sectionIcon} />
                    About Base Buddy
                </h2>
                <p>
                    Base Buddy is a product of <strong>Strategi Consulting, LLC</strong>, a federal contractor providing conversational AI
                    services to U.S. government clients. Base Buddy is deployed at participating military installations to help service
                    members and families access base information quickly.
                </p>
            </section>

            {/* Contact Information */}
            <section>
                <h2>
                    <Mail24Regular className={styles.sectionIcon} />
                    Contact Information
                </h2>
                <p>
                    For questions about this SMS service, contact <a href="mailto:hello@basebuddy.ai">hello@basebuddy.ai</a>.
                </p>
            </section>
        </main>
    );
};

export default SmsTerms;
